import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ImageIcon, MessageSquare, VideoIcon } from "lucide-react";
import { useSelector } from "react-redux";

export const ConversationCard = ({ conversation, userRole, onClick }) => {
  const { user } = useSelector((state) => state.auth);
  const getDisplayName = () => {
    if (userRole === "buyer") {
      return (
        conversation.otherUserDetail?.companyName ||
        conversation.otherUserData?.name
      );
    }
    return conversation.otherUserDetail?.name || "Unknown Buyer";
  };

  const getAvatarSrc = () => {
    if (userRole === "buyer") {
      return (
        conversation.partInfo?.imageUrls?.[0] ||
        `https://images.unsplash.com/photo-1588554480344-a83ee3f9179a?ixlib=rb-4.0.3&q=85&fm=jpg&crop=entropy&cs=srgb&w=100&h=100`
      );
    }
    return conversation.otherUserDetail?.logoUrl || null;
  };

  const getAvatarFallback = () => {
    if (userRole === "buyer") {
      return conversation.partInfo?.name?.charAt(0).toUpperCase() || "P";
    }
    return conversation.otherUserDetail?.name?.charAt(0).toUpperCase() || "B";
  };

  return (
    <Card
      key={`${conversation.partId}-${conversation.otherUserId}`}
      className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-transparent hover:border-primary"
    >
      <CardContent className="p-4 flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <Avatar className="h-12 w-12 border shrink-0">
            <AvatarImage
              src={getAvatarSrc()}
              alt={conversation.partData?.name}
            />
            <AvatarFallback className="bg-muted text-muted-foreground">
              {getAvatarFallback()}
            </AvatarFallback>
          </Avatar>

          <div className="flex-grow min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <h3 className="font-semibold text-base sm:text-lg text-primary truncate">
                {conversation?.partInfo?.name
                  ? conversation?.partInfo?.name == "other"
                    ? conversation?.partInfo?.sub_name
                    : conversation?.partInfo?.name
                  : `${conversation?.partName}-(Inquiry)`}
                {conversation?.count?.[user?.uid] > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-red-500 text-white">
                    {conversation.count[user.uid]}
                  </span>
                )}
              </h3>
              <span className="text-xs text-muted-foreground sm:ml-3">
                {new Date(
                  conversation?.updatedAt?.seconds * 1000
                ).toLocaleDateString()}
              </span>
            </div>

            <div className="flex-grow min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground mt-1 truncate">
                  {userRole === "buyer" ? "With: " : "From: "}
                  <span className="font-medium text-foreground">
                    {getDisplayName()}
                  </span>
                </p>

                <span className="text-xs mt-1 sm:mt-0 text-muted-foreground italic sm:ml-3">
                  {(() => {
                    const userRead = conversation?.isRead?.find(
                      (r) => r.readBy === conversation.otherUserDetail?.id
                    );

                    if (userRead?.readAt) {
                      const date = userRead.readAt.toDate();
                      const diffMs = Date.now() - date.getTime();
                      const diffMinutes = Math.floor(diffMs / (1000 * 60));

                      if (diffMinutes < 1) return "Read just now";
                      if (diffMinutes < 60)
                        return `Read ${diffMinutes} min ago`;

                      const diffHours = Math.floor(diffMinutes / 60);
                      if (diffHours < 24) return `Read ${diffHours}h ago`;

                      const diffDays = Math.floor(diffHours / 24);
                      if (diffDays === 1) return "Read yesterday";
                      if (diffDays < 7) return `Read ${diffDays}d ago`;

                      return `Read on ${date.toLocaleDateString()}`;
                    }

                    return null;
                  })()}
                </span>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mt-1 truncate">
              {conversation?.lastMessage === "image" ? (
                <ImageIcon />
              ) : conversation?.lastMessage === "video" ? (
                <VideoIcon />
              ) : conversation?.lastMessage === "media" ? (
                <>
                  <div className="flex items-center gap-1">
                    {" "}
                    <ImageIcon /> <VideoIcon />
                  </div>
                </>
              ) : (
                conversation?.lastMessage
              )}
            </p>
          </div>
        </div>

        <div className="flex sm:justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="text-primary w-full sm:w-auto mt-1 sm:mt-0"
            onClick={() => onClick(conversation)}
          >
            <MessageSquare className="h-4 w-4 mr-1" /> View
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
